'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import SearchBar from './SearchBar';
import FilterChips from './FilterChips';
import MobileFilterPanel from './MobileFilterPanel';
import ProductGrid from './ProductGrid';
import LoadingSkeleton from './LoadingSkeleton';
import { createClient } from '@/lib/supabase/client';
import { useSiteContent } from '@/lib/content/SiteContentContext';
import { restoreScrollIfPending } from '@/lib/scrollRestoration';

interface Product {
  id: number;
  name: string;
  price: number;
  scentType: string;
  image: string;
  alt: string;
  topNotes: string[];
  heartNotes: string[];
  baseNotes: string[];
  sizes: number[];
  description: string;
  isFastMoving: boolean;
  isBestSelling: boolean;
  stock: number | null;
  offerDiscount?: number;
  gender?: string;
  displayPrice?: number;
  displayCurrency?: string;
}

const ShopCatalogInteractive: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);

  const searchParams = useSearchParams();
  const router = useRouter();
  const shopHeading = useSiteContent('shop_page_heading', 'Luxury Perfume Collection');
  const btnFilters = useSiteContent('shop_btn_filters', 'Filters');
  const btnClearFilters = useSiteContent('shop_btn_clear_filters', 'Clear All Filters');
  const showingTextTpl = useSiteContent('shop_showing_text', 'Showing {count} of {total} perfumes');
  // Fetch products + active product offers + country-specific prices in parallel
  useEffect(() => {
    async function loadProducts() {
      const supabase = createClient();
      const userCountry = typeof window !== 'undefined'
        ? (localStorage.getItem('detected_country_name') || 'United Arab Emirates')
        : 'United Arab Emirates';

      const [{ data }, offersRes, { data: cpData }] = await Promise.all([
        supabase
          .from('products')
          .select('*, product_sizes(*), scent_notes(*)')
          .eq('is_active', true)
          .order('created_at', { ascending: false }),
        fetch('/api/offers').then((r) => r.json()).catch(() => []),
        supabase
          .from('product_country_prices')
          .select('product_id, price, currency_code')
          .eq('country_name', userCountry),
      ]);

      // Build map: product_id → lowest country price for this country
      const cpMap: Record<string, { price: number; currency_code: string }> = {};
      if (cpData) {
        for (const cp of cpData as { product_id: string; price: number; currency_code: string }[]) {
          const key = String(cp.product_id);
          if (!cpMap[key] || cp.price < cpMap[key].price) {
            cpMap[key] = { price: cp.price, currency_code: cp.currency_code };
          }
        }
      }

      // Build map: product_id → discount_percent for active single-product offers
      const offerMap: Record<string, number> = {};
      if (Array.isArray(offersRes)) {
        for (const offer of offersRes) {
          if (offer.offer_type === 'product' && offer.is_active && offer.discount_percent) {
            for (const lp of offer.offer_products || []) {
              if (lp.product_id) offerMap[lp.product_id] = offer.discount_percent;
            }
          }
        }
      }

      if (data) {
        setProducts(
          data.map((p) => {
            const cp = cpMap[String(p.id)];
            return {
              id: p.id,
              name: p.name,
              price: p.price,
              scentType: p.scent_type || '',
              image: p.image_url || '',
              alt: p.image_alt || p.name,
              topNotes: (p.scent_notes || [])
                .filter((n: { note_type: string }) => n.note_type === 'top')
                .map((n: { note_name: string }) => n.note_name),
              heartNotes: (p.scent_notes || [])
                .filter((n: { note_type: string }) => n.note_type === 'heart')
                .map((n: { note_name: string }) => n.note_name),
              baseNotes: (p.scent_notes || [])
                .filter((n: { note_type: string }) => n.note_type === 'base')
                .map((n: { note_name: string }) => n.note_name),
              sizes: (p.product_sizes || []).map((s: { volume_ml: number }) => s.volume_ml),
              description: p.description || '',
              isFastMoving: !!p.is_fast_moving,
              isBestSelling: !!p.is_best_selling,
              stock: p.stock ?? null,
              offerDiscount: offerMap[String(p.id)],
              gender: p.gender || '',
              displayPrice: cp?.price,
              displayCurrency: cp?.currency_code,
            };
          })
        );
      }
      setIsLoading(false);
    }
    loadProducts();
  }, []);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (isLoading) return;
    restoreScrollIfPending();
  }, [isLoading]);

  useEffect(() => {
    if (!isHydrated) return;

    const query = searchParams.get('search') || '';
    const filters = searchParams.get('filters')?.split(',').filter(Boolean) || [];

    setSearchQuery(query);
    setSelectedFilters(filters);
  }, [isHydrated, searchParams]);

  const filteredProducts = useMemo(() => {
    let filtered = products;

    if (searchQuery) {
      filtered = filtered.filter((product) =>
      product.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedFilters.length > 0) {
      filtered = filtered.filter((product) =>
      selectedFilters.includes(product.scentType)
      );
    }

    return filtered;
  }, [searchQuery, selectedFilters, products]);

  const filterCounts = useMemo(() => {
    const counts: Record<string, number> = {
      Floral: 0,
      Woody: 0,
      Fresh: 0,
      Oriental: 0
    };

    const searchFiltered = searchQuery ?
    products.filter((product) =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase())
    ) :
    products;

    searchFiltered.forEach((product) => {
      if (counts[product.scentType] !== undefined) {
        counts[product.scentType]++;
      }
    });

    return counts;
  }, [searchQuery, products]);

  const handleSearchChange = (query: string) => {
    if (!isHydrated) return;

    setSearchQuery(query);
    setIsLoading(true);

    const params = new URLSearchParams();
    if (query) params.set('search', query);
    if (selectedFilters.length > 0) params.set('filters', selectedFilters.join(','));

    router.push(`/shop-catalog${params.toString() ? `?${params.toString()}` : ''}`, { scroll: false });

    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  const handleFilterToggle = (filter: string) => {
    if (!isHydrated) return;

    setIsLoading(true);

    const newFilters = selectedFilters.includes(filter) ?
    selectedFilters.filter((f) => f !== filter) :
    [...selectedFilters, filter];

    setSelectedFilters(newFilters);

    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (newFilters.length > 0) params.set('filters', newFilters.join(','));

    router.push(`/shop-catalog${params.toString() ? `?${params.toString()}` : ''}`, { scroll: false });

    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  const handleClearAllFilters = () => {
    if (!isHydrated) return;

    setIsLoading(true);
    setSelectedFilters([]);

    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);

    router.push(`/shop-catalog${params.toString() ? `?${params.toString()}` : ''}`, { scroll: false });

    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:249:6" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="249" data-component-end-line="261" data-component-file="ShopCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:250:8" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="250" data-component-end-line="260" data-component-file="ShopCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mx-auto%20max-w-%5B1440px%5D%20px-4%20py-8%20md%3Apx-6%20lg%3Apx-8%22%7D" className="mx-auto max-w-[1440px] px-4 py-8 md:px-6 lg:px-8">
          <div data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:251:10" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="251" data-component-end-line="258" data-component-file="ShopCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-8%22%7D" className="mb-8">
            <div data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:252:12" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="252" data-component-end-line="252" data-component-file="ShopCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%20h-12%20w-full%20animate-pulse%20rounded-lg%20bg-muted%22%7D" className="mb-6 h-12 w-full animate-pulse rounded-lg bg-muted" />
            <div data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:253:12" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="253" data-component-end-line="257" data-component-file="ShopCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-3%22%7D" className="flex gap-3">
              {[...Array(4)].map((_, i) =>
              <div data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:255:14" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="255" data-component-end-line="255" data-component-file="ShopCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-10%20w-24%20animate-pulse%20rounded-full%20bg-muted%22%7D" key={i} className="h-10 w-24 animate-pulse rounded-full bg-muted" />
              )}
            </div>
          </div>
          <LoadingSkeleton data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:259:10" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="259" data-component-end-line="259" data-component-file="ShopCatalogInteractive.tsx" data-component-name="LoadingSkeleton" data-component-content="%7B%22elementName%22%3A%22LoadingSkeleton%22%7D" />
        </div>
      </div>);
  }

  return (
    <div data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:265:4" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="265" data-component-end-line="326" data-component-file="ShopCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">

      <div data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:267:6" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="267" data-component-end-line="317" data-component-file="ShopCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mx-auto%20max-w-%5B1440px%5D%20px-4%20py-8%20md%3Apx-6%20lg%3Apx-8%22%7D" className="mx-auto max-w-[1440px] px-4 py-8 md:px-6 lg:px-8">
        <div data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:268:8" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="268" data-component-end-line="314" data-component-file="ShopCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-8%22%7D" className="mb-8">
          <div data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:269:10" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="269" data-component-end-line="285" data-component-file="ShopCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%20flex%20items-center%20justify-between%22%7D" className="mb-6 flex items-center justify-between">
            <h1 data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:270:12" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="270" data-component-end-line="272" data-component-file="ShopCatalogInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22font-heading%20text-3xl%20font-bold%20text-text-primary%20md%3Atext-4xl%22%7D" className="font-heading text-3xl font-bold text-text-primary md:text-4xl">
              {shopHeading}
            </h1>
            <button data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:273:12" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="273" data-component-end-line="284" data-component-file="ShopCatalogInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20gap-2%20rounded-lg%20bg-primary%20px-4%20py-2%20font-body%20text-sm%20font-medium%20text-primary-foreground%20shadow-luxury%20transition-luxury%20hover%3Aopacity-90%20md%3Ahidden%22%7D"
              onClick={() => setIsMobileFilterOpen(true)}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 font-body text-sm font-medium text-primary-foreground shadow-luxury transition-luxury hover:opacity-90 md:hidden">

              <Icon data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:277:14" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="277" data-component-end-line="277" data-component-file="ShopCatalogInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22AdjustmentsHorizontalIcon%22%7D" name="AdjustmentsHorizontalIcon" size={20} />
              {btnFilters}
              {selectedFilters.length > 0 &&
              <span data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:280:14" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="280" data-component-end-line="282" data-component-file="ShopCatalogInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22flex%20h-5%20w-5%20items-center%20justify-center%20rounded-full%20bg-accent%20font-data%20text-xs%20text-accent-foreground%22%7D" className="flex h-5 w-5 items-center justify-center rounded-full bg-accent font-data text-xs text-accent-foreground">
                  {selectedFilters.length}
                </span>
              }
            </button>
          </div>

          <div data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:287:10" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="287" data-component-end-line="292" data-component-file="ShopCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%22%7D" className="mb-6">
            <SearchBar data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:288:12" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="288" data-component-end-line="291" data-component-file="ShopCatalogInteractive.tsx" data-component-name="SearchBar" data-component-content="%7B%22elementName%22%3A%22SearchBar%22%7D"
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              resultCount={filteredProducts.length} />
          </div>

          <div data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:294:10" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="294" data-component-end-line="307" data-component-file="ShopCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%20hidden%20items-center%20justify-between%20md%3Aflex%22%7D" className="mb-6 hidden items-center justify-between md:flex">
            <FilterChips data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:295:12" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="295" data-component-end-line="298" data-component-file="ShopCatalogInteractive.tsx" data-component-name="FilterChips" data-component-content="%7B%22elementName%22%3A%22FilterChips%22%7D"
              selectedFilters={selectedFilters}
              onFilterToggle={handleFilterToggle}
              filterCounts={filterCounts} />

            {selectedFilters.length > 0 &&
            <button data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:301:12" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="301" data-component-end-line="305" data-component-file="ShopCatalogInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22font-body%20text-sm%20font-medium%20text-primary%20transition-luxury%20hover%3Atext-accent%22%7D"
              onClick={handleClearAllFilters}
              className="font-body text-sm font-medium text-primary transition-luxury hover:text-accent">
                {btnClearFilters}
              </button>
            }
          </div>

          <div data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:309:10" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="309" data-component-end-line="313" data-component-file="ShopCatalogInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-4%20flex%20items-center%20justify-between%22%7D" className="mb-4 flex items-center justify-between">
            <p data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:310:12" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="310" data-component-end-line="312" data-component-file="ShopCatalogInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-body%20text-sm%20text-text-secondary%22%7D" className="font-body text-sm text-text-secondary">
              {showingTextTpl.replace('{count}', String(filteredProducts.length)).replace('{total}', String(products.length))}
            </p>
          </div>
        </div>

        {isLoading ? <LoadingSkeleton data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:316:21" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="316" data-component-end-line="316" data-component-file="ShopCatalogInteractive.tsx" data-component-name="LoadingSkeleton" data-component-content="%7B%22elementName%22%3A%22LoadingSkeleton%22%7D" /> : <ProductGrid data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:316:43" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="316" data-component-end-line="316" data-component-file="ShopCatalogInteractive.tsx" data-component-name="ProductGrid" data-component-content="%7B%22elementName%22%3A%22ProductGrid%22%7D" products={filteredProducts} />}
      </div>

      <MobileFilterPanel data-component-id="src/app/shop-catalog/components/ShopCatalogInteractive.tsx:319:6" data-component-path="src/app/shop-catalog/components/ShopCatalogInteractive.tsx" data-component-line="319" data-component-end-line="325" data-component-file="ShopCatalogInteractive.tsx" data-component-name="MobileFilterPanel" data-component-content="%7B%22elementName%22%3A%22MobileFilterPanel%22%7D"
        isOpen={isMobileFilterOpen}
        onClose={() => setIsMobileFilterOpen(false)}
        selectedFilters={selectedFilters}
        onFilterToggle={handleFilterToggle}
        filterCounts={filterCounts}
        onClearAll={handleClearAllFilters} />
    </div>);
};

export default ShopCatalogInteractive;
